'use client';

import { c as _c } from "react/compiler-runtime";
import type { MultiSelectAnswer } from '@repo/contracts/cardfinder/funnel/types';
import { css, cx } from '@repo/pandacss/css';
import { visuallyHidden } from '@repo/pandacss/patterns';
import { Typography } from '@repo/ui/Typography';
import { useId } from 'react';
import type { MultiSelectStep as MultiSelectStepConfig } from '@/features/funnel/config/types';
import { useAnswer } from '@/features/funnel/state/answer/hooks';
import { FUNNEL_STEP_TITLE_ID } from '../../Wrapper/StepTitle';
import { choiceListClass, choiceListItemClass, getChoiceSetPositionProps } from '../choiceGroupA11y';
import { OptionCardContent } from '../SingleSelectInput/OptionCard';
import { optionCardClass, optionCardDisabledStyles, optionCardSelectedStyles } from '../SingleSelectInput/styles';
type MultiSelectInputProps = {
  step: MultiSelectStepConfig;
};
export function MultiSelectInput(t0) {
  const $ = _c(35);
  const {
    step
  } = t0;
  const {
    value,
    setAnswer
  } = useAnswer(step.id);
  let t1;
  if ($[0] !== value) {
    t1 = value ?? [];
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const arr = t1;
  const max = step.maxSelections;
  const reachedMax = max !== undefined && arr.length >= max;
  const hintId = useId();
  const statusId = useId();
  let t2;
  if ($[2] !== arr || $[3] !== setAnswer) {
    t2 = optionValue => {
      if (arr.includes(optionValue)) {
        setAnswer(arr.filter(v => v !== optionValue));
      } else {
        setAnswer([...arr, optionValue]);
      }
    };
    $[2] = arr;
    $[3] = setAnswer;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggle = t2;
  const t3 = max !== undefined ? hintId : null;
  let t4;
  if ($[5] !== statusId || $[6] !== t3) {
    t4 = [t3, statusId].filter(Boolean);
    $[5] = statusId;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const describedBy = t4.join(" ");
  const t5 = describedBy || undefined;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css({
      border: "none",
      padding: "0",
      margin: "0"
    });
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== hintId || $[10] !== max) {
    t7 = max !== undefined && <Typography as="p" id={hintId} variant="body" css={{
      color: "neutral.darker",
      fontWeight: "medium",
      mb: "2"
    }}>Select up to {max}</Typography>;
    $[9] = hintId;
    $[10] = max;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = visuallyHidden();
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = max !== undefined ? `${arr.length} of ${max} selected` : `${arr.length} selected`;
  let t10;
  if ($[13] !== statusId || $[14] !== t9) {
    t10 = <p role="status" aria-live="polite" id={statusId} className={t8}>{t9}</p>;
    $[13] = statusId;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== arr || $[17] !== reachedMax || $[18] !== step.id || $[19] !== step.options || $[20] !== toggle) {
    let t12;
    if ($[22] !== arr || $[23] !== reachedMax || $[24] !== step.id || $[25] !== step.options.length || $[26] !== toggle) {
      t12 = (option, index) => {
        const checked = arr.includes(option.value);
        const disabled = !checked && reachedMax;
        let cls = optionCardClass;
        if (checked) {
          cls = `${optionCardClass} ${optionCardSelectedStyles}`;
        }
        if (disabled) {
          cls = `${cls} ${optionCardDisabledStyles}`;
        }
        const iconHTML = option.icon;
        return <li key={option.value} className={choiceListItemClass}><label className={cls}><input type="checkbox" name={step.id} tabIndex={0} value={option.value} checked={checked} disabled={disabled} onChange={() => toggle(option.value)} {...getChoiceSetPositionProps(index, step.options.length)} className={cx(!iconHTML ? css({
              "&:checked + span": {
                bg: "blues.main",
                borderColor: "blues.focus"
              }
            }) : "", visuallyHidden())} />{!iconHTML && <span aria-hidden="true" className={css({
              flexShrink: "[0]",
              width: "[20px]",
              height: "[20px]",
              rounded: "sm",
              border: "[1px solid {colors.neutral.dark}]",
              bg: "common.white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "common.white",
              transition: "all",
              fontSize: "[14px]",
              fontWeight: "bold",
              lineHeight: "[1]",
              fontFamily: "gotham"
            })}>{checked ? "\u2713" : ""}</span>}<OptionCardContent label={option.label} iconHTML={iconHTML} description={option.description} /></label></li>;
      };
      $[22] = arr;
      $[23] = reachedMax;
      $[24] = step.id;
      $[25] = step.options.length;
      $[26] = toggle;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t11 = step.options.map(t12);
    $[16] = arr;
    $[17] = reachedMax;
    $[18] = step.id;
    $[19] = step.options;
    $[20] = toggle;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <ul className={choiceListClass}>{t11}</ul>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t5 || $[33] !== t7) {
    t13 = <fieldset aria-labelledby={FUNNEL_STEP_TITLE_ID} aria-describedby={t5} className={t6}>{t7}{t10}{t12}</fieldset>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t5;
    $[33] = t7;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}